import { Typography } from '@euler/app/components/typography';
import { LevelBars } from '@euler/components/voice-input/level/LevelBars';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { Pause, Play } from 'lucide-react-native';
import { memo } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { aiColors } from '../../../../../ai-suite/theme';
import { formatVoiceDuration } from '../../../functions/formatVoiceDuration';
import { TraineeVoice } from '../../../functions/traineeVoice';

const kFaint = 'rgba(255,255,255,0.75)';

/**
 * The trainee's own line, on the right; a spoken one carries how it
 * sounded and how long it took, and plays back while its audio is kept.
 */
export const TraineeBubble = memo(
  ({
    text,
    voice,
    playing,
    onPlay,
  }: {
    text: string;
    /** A spoken line's waveform (`waveformBars` levels) and length. */
    voice: Pick<TraineeVoice, 'levels' | 'durationMs'> | null;
    playing: boolean;
    /** Plays or stops its voice; null when there is none to hear. */
    onPlay: (() => void) | null;
  }) => {
    const { rpx } = useResponsiveContext();
    const Icon = playing ? Pause : Play;
    return (
      <View
        style={{
          alignSelf: 'flex-end',
          maxWidth: '78%',
          gap: rpx(6),
          borderRadius: rpx(22),
          borderBottomRightRadius: rpx(6),
          backgroundColor: aiColors.primary,
          paddingHorizontal: rpx(14),
          paddingVertical: rpx(10),
        }}
      >
        {voice ? (
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={playing ? '停止' : '播放'}
            disabled={!onPlay}
            onPress={() => onPlay?.()}
            hitSlop={6}
            style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(8) }}
          >
            {onPlay ? (
              <Icon
                size={rpx(14)}
                color={aiColors.primaryForeground}
                fill={aiColors.primaryForeground}
              />
            ) : null}
            <LevelBars levels={voice.levels} color={kFaint} />
            <Typography.Text
              size={rpx(12)}
              color={kFaint}
              style={{ fontVariant: ['tabular-nums'] }}
            >
              {formatVoiceDuration(
                Math.max(1, Math.ceil(voice.durationMs / 1000)),
              )}
            </Typography.Text>
          </TouchableOpacity>
        ) : null}
        <Typography.Text
          size={rpx(14)}
          color={aiColors.primaryForeground}
          style={{ lineHeight: rpx(21) }}
        >
          {text}
        </Typography.Text>
      </View>
    );
  },
);
